import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { User } from '../../types';
import { storage } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  Container, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  SelectChangeEvent,
  Avatar,
  Checkbox,
  ListItemText,
  ImageList,
  ImageListItem,
  IconButton
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { differenceInYears, format } from 'date-fns';

const ProfileForm: React.FC = () => {
  const { user, updateUserProfile } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<Partial<User>>({
    displayName: '',
    age: '',
    gender: '',
    location: '',
    bio: '',
    interests: '',
    occupation: '',
    education: '',
    lookingFor: '',
    height: '',
    genderPreference: '',
    ethnicities: [],
    nationality: [],
    birthdate: '',
  });
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const navigate = useNavigate();

  const heightOptions = Array.from({ length: 40 }, (_, i) => {
    const feet = Math.floor((i + 50) / 12);
    const inches = (i + 50) % 12;
    return {
      value: `${i + 50}`,
      label: `${feet}'${inches}"`
    };
  });

  const countries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
    "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
    "Denmark", "Djibouti", "Dominica", "Dominican Republic",
    "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
    "Fiji", "Finland", "France",
    "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
    "Haiti", "Honduras", "Hong Kong", "Hungary",
    "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast",
    "Jamaica", "Japan", "Jordan",
    "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
    "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
    "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
    "Oman",
    "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
    "Qatar",
    "Romania", "Russia", "Rwanda",
    "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
    "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
    "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
    "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
    "Yemen",
    "Zambia", "Zimbabwe"
  ];

  useEffect(() => {
    if (user) {
      setFormData(prevData => ({
        ...prevData,
        ...user,
        birthdate: user.birthdate || ''
      }));
      
      // Initialize image previews with existing photos
      const existingPhotos = [
        user.profilePicture,
        ...(user.additionalPhotos || [])
      ].filter(Boolean) as string[];
      
      setImagePreviews(existingPhotos);
    }
  }, [user]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const files = Array.from(e.target.files);
      const totalPhotos = imageFiles.length + files.length + imagePreviews.length;
      
      if (totalPhotos > 6) {
        setError('Maximum 6 photos allowed');
        return;
      }
      
      setImageFiles(prev => [...prev, ...files]);
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setImagePreviews(prev => [...prev, ...newPreviews]);
    }
  };

  const handleRemoveImage = (index: number) => {
    // If it's a new file (has corresponding imageFile)
    if (index >= imagePreviews.length - imageFiles.length) {
      const fileIndex = index - (imagePreviews.length - imageFiles.length);
      setImageFiles(prev => prev.filter((_, i) => i !== fileIndex));
      URL.revokeObjectURL(imagePreviews[index]);
    }
    
    // Remove from previews
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    
    // If it's an existing photo, we'll handle the deletion in handleSubmit
  };

  const uploadImages = async (): Promise<string[]> => {
    if (!user?.uid) return [];
    
    // Keep existing photos that weren't removed
    const existingPhotos = [
      user.profilePicture,
      ...(user.additionalPhotos || [])
    ].filter(Boolean) as string[];
    
    const keptPhotos = existingPhotos.filter(url => 
      imagePreviews.includes(url)
    );
    
    // Upload new photos
    const uploadPromises = imageFiles.map(async (file) => {
      const profileStorageRef = ref(storage, `profile-pictures/${user.uid}/${Date.now()}`);
      await uploadBytes(profileStorageRef, file);
      return getDownloadURL(profileStorageRef);
    });
    
    const newPhotoUrls = await Promise.all(uploadPromises);
    return [...keptPhotos, ...newPhotoUrls];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      if (!user?.uid) throw new Error('No user logged in');
      
      let profileData = { ...formData };
      const photoUrls = await uploadImages();
      
      if (photoUrls.length > 0) {
        profileData.profilePicture = photoUrls[0];  // First photo is main profile picture
        profileData.additionalPhotos = photoUrls.slice(1);  // Rest are additional photos
      }
      
      await updateUserProfile(user.uid, profileData);
      setSuccess(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleMultipleSelect = (event: SelectChangeEvent<string[]>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleCancel = () => {
    navigate('/', { replace: true });
  };

  const calculateAge = (birthdate: Date): string => {
    const age = differenceInYears(new Date(), birthdate);
    return age.toString();
  };

  const handleBirthdateChange = (date: Date | null) => {
    if (!date) return;

    const minAge = 18;
    const maxAge = 100;
    const age = differenceInYears(new Date(), date);

    if (age < minAge) {
      setError(`You must be at least ${minAge} years old to register`);
      return;
    }

    if (age > maxAge) {
      setError(`Please enter a valid birthdate`);
      return;
    }

    setFormData(prev => ({
      ...prev,
      birthdate: date.toISOString(),
      age: age.toString()
    }));
    setError('');
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {user?.displayName ? 'Edit Profile' : 'Create Profile'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Profile updated successfully!</Alert>}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              <ImageList 
                sx={{ 
                  width: '100%', 
                  height: 'auto',
                  '& .MuiImageListItem-root': {
                    paddingTop: '133%', // Make items more vertical
                    position: 'relative'
                  }
                }} 
                cols={3} 
                rowHeight="auto"
              >
                {imagePreviews.map((preview, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={preview}
                      alt={`Profile photo ${index + 1}`}
                      loading="lazy"
                      style={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                        zIndex: 1
                      }}
                      onClick={() => handleRemoveImage(index)}
                    >
                      <DeleteIcon sx={{ color: 'white' }} />
                    </IconButton>
                    {index === 0 && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: 8,
                          color: 'white',
                          bgcolor: 'rgba(0, 0, 0, 0.5)',
                          px: 1,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          zIndex: 1
                        }}
                      >
                        Main Photo
                      </Typography>
                    )}
                  </ImageListItem>
                ))}
              </ImageList>
              
              {imagePreviews.length < 6 && (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<AddAPhotoIcon />}
                  sx={{ mt: 2 }}
                >
                  {imagePreviews.length === 0 ? 'Upload Photos' : 'Add More Photos'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                    multiple
                  />
                </Button>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Upload up to 6 photos. First photo will be your profile picture.
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Display Name"
              name="displayName"
              value={formData.displayName || ''}
              onChange={handleChange}
              required
            />

            {formData.age && (
              <Typography variant="body2" color="text.secondary">
                Age: {formData.age} years old
              </Typography>
            )}

            <FormControl fullWidth required>
              <InputLabel>Height</InputLabel>
              <Select
                name="height"
                value={formData.height || ''}
                label="Height"
                onChange={handleChange}
              >
                {heightOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Gender</InputLabel>
              <Select
                name="gender"
                value={formData.gender || ''}
                label="Gender"
                onChange={handleChange}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Nonbinary</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Interested In</InputLabel>
              <Select
                name="genderPreference"
                value={formData.genderPreference || ''}
                label="Interested In"
                onChange={handleChange}
              >
                <MenuItem value="men">Men</MenuItem>
                <MenuItem value="women">Women</MenuItem>
                <MenuItem value="nonbinary">Nonbinary People</MenuItem>
                <MenuItem value="everyone">Everyone</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Ethnicity</InputLabel>
              <Select
                multiple
                name="ethnicities"
                value={formData.ethnicities || []}
                onChange={handleMultipleSelect}
                label="Ethnicities"
                renderValue={(selected) => {
                  const ethnicityMap: { [key: string]: string } = {
                    "black": "Black",
                    "eastAsian": "East Asian",
                    "white": "White",
                    "middleEastern": "Middle Eastern",
                    "southAsian": "South Asian",
                    "latinAmerican": "Hispanic/Latino",
                    "indigenous": "Indigenous",
                    "other": "Other"
                  };
                  return (selected as string[]).map(value => ethnicityMap[value]).join(', ');
                }}
              >
                {[
                  { value: "black", label: "Black" },
                  { value: "eastAsian", label: "East Asian" },
                  { value: "white", label: "White" },
                  { value: "middleEastern", label: "Middle Eastern" },
                  { value: "southAsian", label: "South Asian" },
                  { value: "latinAmerican", label: "Hispanic/Latino" },
                  { value: "indigenous", label: "Indigenous" },
                  { value: "other", label: "Other" }
                ].map((ethnicity) => (
                  <MenuItem key={ethnicity.value} value={ethnicity.value}>
                    <Checkbox checked={(formData.ethnicities || []).indexOf(ethnicity.value) > -1} />
                    <ListItemText primary={ethnicity.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Current Citizenship</InputLabel>
              <Select
                multiple
                name="nationality"
                value={formData.nationality || []}
                onChange={handleMultipleSelect}
                label="Nationality"
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    <Checkbox checked={(formData.nationality || []).indexOf(country) > -1} />
                    <ListItemText primary={country} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Location"
              name="location"
              value={formData.location || ''}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Bio"
              name="bio"
              multiline
              rows={4}
              value={formData.bio || ''}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Interests"
              name="interests"
              value={formData.interests || ''}
              onChange={handleChange}
              helperText="Separate interests with commas"
              required
            />

            <TextField
              fullWidth
              label="Occupation"
              name="occupation"
              value={formData.occupation || ''}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Education"
              name="education"
              value={formData.education || ''}
              onChange={handleChange}
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Looking For</InputLabel>
              <Select
                name="lookingFor"
                value={formData.lookingFor || ''}
                label="Looking For"
                onChange={handleChange}
              >
                <MenuItem value="relationship">Relationship</MenuItem>
                <MenuItem value="friendship">Friendship</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              {user?.displayName && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleCancel}
                  fullWidth
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{ color: 'white' }}
              >
                {user?.displayName ? 'Update Profile' : 'Create Profile'}
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      </Box>
    </Container>
  );
};

export default ProfileForm;